import { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import * as organizationSelectors from '../selectors/organizationSelectors';
import { useLastEvent, useActiveUser, useInboxParams, useSelectThreadEvents } from '.';
import { EventHelpers, DataHelpers } from '../helpers';
import { setInboxCommunicationContext } from '../reducers/inboxReducer';
import resolveInboxCommunicationContext from '../helpers/InboxCommunicationHelper';

const useInboxCommunicationContext = () => {
  const dispatch = useDispatch();
  const { groupId } = useInboxParams();
  const location = useLocation();
  const userOrganization = useSelector((state) => organizationSelectors.getCurrentOrg(state));
  const { mostRecentEvent } = useLastEvent();
  const activeUser = useActiveUser();
  const loggedInUser = useSelector((state) => state.user.users[state.auth.currentUser]);
  const channels = useSelector((state) => state.channel.channels);
  const groups = useSelector((state) => state.group.groups);
  const threadFromChannelIds = useSelector((state) => state.inbox.threadFromChannelIds);
  const phones = Object.values(useSelector((state) => state.phone.phones));
  const facebooks = useSelector((state) => state.facebook.facebooks);
  const instagrams = useSelector((state) => state.instagram.instagrams);
  const rhinograms = useSelector((state) => state.rhinogram.rhinograms);
  const selectedEventsQueryResult = useSelectThreadEvents();

  const secureEventIds = selectedEventsQueryResult?.data?.ids?.filter((id) => {
    const event = selectedEventsQueryResult?.data?.entities?.[id];
    return EventHelpers.isSecureEvent(event);
  });

  const messageEventIds = selectedEventsQueryResult?.data?.ids?.filter((id) => {
    const event = selectedEventsQueryResult?.data?.entities?.[id];
    return EventHelpers.isMessageEvent(event);
  });

  const mostRecentMessageEvent = EventHelpers.shapeEventToV3(selectedEventsQueryResult?.data?.entities?.[messageEventIds?.[messageEventIds.length - 1]]);
  const mostRecentSecureEvent = EventHelpers.shapeEventToV3(selectedEventsQueryResult?.data?.entities?.[secureEventIds?.[secureEventIds.length - 1]]);

  const isAssignedInbox = location?.pathname?.includes('inbox/user');

  const context = resolveInboxCommunicationContext({
    activeUser,
    loggedInUser,
    userOrganization,
    channels,
    groups,
    threadFromChannelIds,
    phones,
    facebooks,
    instagrams,
    rhinograms,
    mostRecentEvent,
    mostRecentMessageEvent,
    mostRecentSecureEvent,
    groupId,
    isAssignedInbox,
  });

  // Only dispatch when the inputs that actually drive resolution change:
  // the active conversation (activeUser) or the most-recent event in this
  // thread. `context` itself is a fresh object every render, so listing it
  // in deps would fire this effect on every render — which would clobber
  // any channel the user explicitly picked from the compose dropdown.
  // Auto-follow on new inbound events still works because mostRecentEvent.id
  // changes when a new event lands.
  useEffect(() => {
    if (DataHelpers.hasData(context)) {
      dispatch(setInboxCommunicationContext(context));
    }
  }, [activeUser?.id, mostRecentEvent?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  return context;
};

export default useInboxCommunicationContext;
